// MultiProductCreator.jsx
// Multi-product creation with tabs, queue, and templates
// IMPROVED: Better edge case handling, confirmation dialogs, status sync

import React, { useState, useCallback, useEffect, useMemo, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Copy,
  X,
  Loader2,
  Save,
  Layers,
  FileText,
  ArrowLeft,
  MoreHorizontal,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Edit3,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";

// ✅ UPDATE THESE IMPORTS based on your project structure
import { createProductThunk } from "../../../store/thunks/adminProductThunks";
import { getAllCategoriesThunk } from "../../../store/thunks/categoryThunks";
import { getBranchesThunk } from "../../../store/thunks/branchThunks";

// Import context from separate file
import { MultiProductContext } from "../../../context/MultiProductContext";

import QuickProductForm from "./QuickProductForm";
import SaveQueuePanel from "./SaveQueuePanel";
import ProductTemplates from "./ProductTemplates";
import { useProductQueue } from "./hooks/useProductQueue";
import {
  useProductTabs,
  generateDefaultProduct,
  TAB_STATUS,
} from "./hooks/useProductTabs";
import { useAutoSave } from "./hooks/useAutoSave";

// Re-export for convenience
export { useMultiProduct } from "../../../context/MultiProductContext";

const MultiProductCreator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { user } = useSelector((state) => state.auth);
  const { categories } = useSelector((state) => state.category);
  const { branches } = useSelector((state) => state.branch);

  // Get primary branch from user
  const primaryBranch = useMemo(() => {
    return user?.branchAccess?.primaryBranch || "";
  }, [user?.branchAccess?.primaryBranch]);

  // Fetch on mount
  useEffect(() => {
    if (!categories?.length) dispatch(getAllCategoriesThunk());
    if (!branches?.length) dispatch(getBranchesThunk());
  }, [dispatch, categories?.length, branches?.length]);

  // Hooks
  const {
    tabs,
    activeTabId,
    addTab,
    removeTab,
    forceRemoveTab,
    duplicateTab,
    updateTabData,
    setActiveTab,
    getActiveTab,
    updateTabStatus,
    markForReSave,
    bulkDuplicate,
    getSaveableTabs,
    getAttentionCount,
    resetTab,
    hasUnsavedChanges,
  } = useProductTabs(primaryBranch);

  // Status change handler for queue -> tabs sync
  const handleQueueStatusChange = useCallback(
    (tabId, status, error, savedProductId) => {
      // Map queue status to tab status
      const statusMap = {
        pending: TAB_STATUS.QUEUED,
        processing: TAB_STATUS.SAVING,
        completed: TAB_STATUS.SAVED,
        failed: TAB_STATUS.FAILED,
        edited: TAB_STATUS.EDITED,
      };
      const tabStatus = statusMap[status] || status;
      updateTabStatus(tabId, tabStatus, error, savedProductId);
    },
    [updateTabStatus]
  );

  const {
    queue,
    addToQueue,
    updateQueueItemData,
    removeFromQueue,
    retryItem,
    retryWithData,
    pauseQueue,
    resumeQueue,
    clearCompleted,
    isProcessing,
    isPaused,
    stats,
    isInQueue,
    getQueueItem,
  } = useProductQueue(dispatch, handleQueueStatusChange);

  const { savedDrafts, saveDraft, loadDraft, clearDrafts } = useAutoSave(tabs);

  // UI State
  const [showTemplates, setShowTemplates] = useState(false);
  const [showQueuePanel, setShowQueuePanel] = useState(false);
  const [showBulkDuplicate, setShowBulkDuplicate] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Media state per tab
  const [tabMediaState, setTabMediaState] = useState({});

  const activeTab = getActiveTab();
  const attentionCount = getAttentionCount();

  // Initialize media for tab
  const initializeTabMedia = useCallback((tabId) => {
    setTabMediaState((prev) => ({
      ...prev,
      [tabId]: {
        displayMedia: [],
        mediaFiles: {
          images: [],
          videos: [],
          existingMedia: [],
          pendingRemovals: [],
        },
      },
    }));
  }, []);

  // Get media for tab
  const getTabMedia = useCallback(
    (tabId) => {
      return (
        tabMediaState[tabId] || {
          displayMedia: [],
          mediaFiles: {
            images: [],
            videos: [],
            existingMedia: [],
            pendingRemovals: [],
          },
        }
      );
    },
    [tabMediaState]
  );

  // Media change handler
  const handleMediaChange = useCallback(
    (newMedia) => {
      if (!activeTabId) return;
      setTabMediaState((prev) => ({
        ...prev,
        [activeTabId]: { ...prev[activeTabId], displayMedia: newMedia },
      }));
    },
    [activeTabId]
  );

  // Media upload handler
  const handleMediaUpload = useCallback(
    async (files) => {
      if (!activeTabId) return [];

      const newImages = [];
      const newVideos = [];
      const newDisplayItems = [];

      for (const file of files) {
        const isVideo = file.type.startsWith("video/");
        if (isVideo) newVideos.push(file);
        else newImages.push(file);

        const preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });

        const currentMedia = tabMediaState[activeTabId]?.displayMedia || [];
        const hasHero = currentMedia.some((m) => m.isHero);

        newDisplayItems.push({
          id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          url: preview,
          type: isVideo ? "video" : "image",
          isNew: true,
          isHero: !hasHero && !isVideo && newDisplayItems.length === 0,
        });
      }

      setTabMediaState((prev) => {
        const current = prev[activeTabId] || {
          displayMedia: [],
          mediaFiles: {
            images: [],
            videos: [],
            existingMedia: [],
            pendingRemovals: [],
          },
        };
        return {
          ...prev,
          [activeTabId]: {
            displayMedia: [...current.displayMedia, ...newDisplayItems],
            mediaFiles: {
              ...current.mediaFiles,
              images: [...current.mediaFiles.images, ...newImages],
              videos: [...current.mediaFiles.videos, ...newVideos],
            },
          },
        };
      });

      return files.map(() => ({ isNew: true }));
    },
    [activeTabId, tabMediaState]
  );

  // Media delete handler
  const handleMediaDelete = useCallback(
    async (mediaId) => {
      if (!activeTabId) return;

      setTabMediaState((prev) => {
        const current = prev[activeTabId];
        if (!current) return prev;

        const mediaItem = current.displayMedia.find(
          (m) => m.id === mediaId || m._id === mediaId
        );
        if (!mediaItem || !mediaItem.isNew) return prev;

        const updatedDisplayMedia = current.displayMedia.filter(
          (m) => m.id !== mediaId && m._id !== mediaId
        );

        if (mediaItem.isHero && updatedDisplayMedia.length > 0) {
          const firstImage = updatedDisplayMedia.find(
            (m) => m.type !== "video"
          );
          if (firstImage) firstImage.isHero = true;
        }

        const updatedImages = [...current.mediaFiles.images];
        const updatedVideos = [...current.mediaFiles.videos];

        const mediaIndex = current.displayMedia
          .filter((m) => m.isNew && m.type === mediaItem.type)
          .findIndex((m) => m.id === mediaId || m._id === mediaId);

        if (mediaItem.type === "video" && mediaIndex !== -1)
          updatedVideos.splice(mediaIndex, 1);
        else if (mediaIndex !== -1) updatedImages.splice(mediaIndex, 1);

        return {
          ...prev,
          [activeTabId]: {
            displayMedia: updatedDisplayMedia,
            mediaFiles: {
              ...current.mediaFiles,
              images: updatedImages,
              videos: updatedVideos,
            },
          },
        };
      });
    },
    [activeTabId]
  );

  // Add tab with media init
  const addTabWithMedia = useCallback(
    (initialData) => {
      const mergedData = initialData
        ? { ...generateDefaultProduct(primaryBranch), ...initialData }
        : null;
      const newTabId = addTab(mergedData);
      initializeTabMedia(newTabId);
      return newTabId;
    },
    [addTab, initializeTabMedia, primaryBranch]
  );

  // Duplicate tab with media init
  const duplicateTabWithMedia = useCallback(
    (tabId) => {
      const newTabId = duplicateTab(tabId);
      initializeTabMedia(newTabId);
      return newTabId;
    },
    [duplicateTab, initializeTabMedia]
  );

  // Handle tab close with confirmation
  const handleTabClose = useCallback(
    (tabId) => {
      const result = removeTab(tabId);

      if (result.needsConfirmation) {
        setConfirmDialog({
          type: "close-tab",
          tabId,
          title: "Unsaved Changes",
          message: `"${result.tabName}" has unsaved changes. Are you sure you want to close it?`,
          confirmText: "Close Anyway",
          cancelText: "Keep Editing",
        });
      }
    },
    [removeTab]
  );

  // Validate product before save
  const validateProduct = useCallback((data) => {
    const errors = [];

    if (!data.name?.trim() || data.name.trim().length < 3) {
      errors.push("Product name is required (min 3 characters)");
    }
    if (!data.type) {
      errors.push("Product type is required");
    }
    if (!data.subType) {
      errors.push("Product subtype is required");
    }
    if (!data.purity) {
      errors.push("Purity is required");
    }

    const grossWeight = parseFloat(data.grossWeight);
    if (isNaN(grossWeight) || grossWeight <= 0) {
      errors.push("Gross weight must be greater than 0");
    }

    const netWeight = parseFloat(data.netWeight);
    if (isNaN(netWeight) || netWeight <= 0) {
      errors.push("Net weight must be greater than 0");
    }
    if (!isNaN(netWeight) && !isNaN(grossWeight) && netWeight > grossWeight) {
      errors.push("Net weight cannot exceed gross weight");
    }

    if (!data.karigarCode?.trim()) {
      errors.push("Karigar code is required");
    }

    return errors;
  }, []);

  // Save product - IMPROVED with validation and duplicate check
  const handleSaveProduct = useCallback(
    (tabId, productData) => {
      // Validate first
      const errors = validateProduct(productData);
      if (errors.length > 0) {
        toast.error(errors[0]); // Show first error
        return false;
      }

      // Check if already in queue
      if (isInQueue(tabId)) {
        // Update queue item with new data instead of blocking
        const media = getTabMedia(tabId);
        const dataWithMedia = {
          ...productData,
          mediaFiles: media.mediaFiles,
        };
        updateQueueItemData(tabId, dataWithMedia, productData.name);
        toast.success("Updated queued item with latest changes");
        return true;
      }

      const media = getTabMedia(tabId);
      const dataWithMedia = {
        ...productData,
        mediaFiles: media.mediaFiles,
      };

      updateTabStatus(tabId, TAB_STATUS.QUEUED);
      const queueItem = addToQueue({
        id: tabId,
        data: dataWithMedia,
        name: productData.name || "Untitled",
      });

      if (queueItem) {
        toast.success(`"${productData.name}" added to save queue`);
        return true;
      }
      return false;
    },
    [
      validateProduct,
      isInQueue,
      getTabMedia,
      updateQueueItemData,
      updateTabStatus,
      addToQueue,
    ]
  );

  // Re-save after editing a failed product
  const handleReSave = useCallback(
    (tabId) => {
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) return;

      const media = getTabMedia(tabId);
      const dataWithMedia = {
        ...tab.data,
        mediaFiles: media.mediaFiles,
      };

      // Reset tab status
      markForReSave(tabId);

      // Add to queue
      addToQueue({
        id: tabId,
        data: dataWithMedia,
        name: tab.data.name || "Untitled",
      });

      toast.success(`"${tab.data.name}" re-added to save queue`);
    },
    [tabs, getTabMedia, markForReSave, addToQueue]
  );

  // Save all valid products
  const handleSaveAll = useCallback(() => {
    const saveableTabs = getSaveableTabs();

    if (saveableTabs.length === 0) {
      toast.error("No valid products to save");
      return;
    }

    let savedCount = 0;
    saveableTabs.forEach((tab) => {
      if (handleSaveProduct(tab.id, tab.data)) {
        savedCount++;
      }
    });

    if (savedCount > 0) {
      toast.success(`${savedCount} products queued for saving`);
    }
  }, [getSaveableTabs, handleSaveProduct]);

  // Bulk duplicate
  const handleBulkDuplicate = useCallback(() => {
    if (activeTab && duplicateCount > 0) {
      const newTabIds = bulkDuplicate(activeTab.id, duplicateCount);
      newTabIds.forEach((tabId) => initializeTabMedia(tabId));
      setShowBulkDuplicate(false);
      toast.success(`Created ${duplicateCount} copies`);
    }
  }, [activeTab, duplicateCount, bulkDuplicate, initializeTabMedia]);

  // Select template
  const handleSelectTemplate = useCallback(
    (template) => {
      const newTabId = addTabWithMedia(template.data);
      setActiveTab(newTabId);
      setShowTemplates(false);
      toast.success(`Loaded: ${template.name}`);
    },
    [addTabWithMedia, setActiveTab]
  );

  // Handle confirm dialog
  const handleConfirmDialogAction = useCallback(
    (confirmed) => {
      if (!confirmDialog) return;

      if (confirmed) {
        switch (confirmDialog.type) {
          case "close-tab":
            forceRemoveTab(confirmDialog.tabId);
            break;
          case "navigate-away":
            // Handle navigation
            break;
        }
      }

      setConfirmDialog(null);
    },
    [confirmDialog, forceRemoveTab]
  );

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges || stats.pending > 0 || stats.saving > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, stats.pending, stats.saving]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "n") {
        e.preventDefault();
        addTabWithMedia();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "d" && activeTab) {
        e.preventDefault();
        duplicateTabWithMedia(activeTab.id);
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === "s" &&
        activeTab &&
        (activeTab.status === TAB_STATUS.DRAFT ||
          activeTab.status === TAB_STATUS.FAILED ||
          activeTab.status === TAB_STATUS.EDITED)
      ) {
        e.preventDefault();
        handleSaveProduct(activeTab.id, activeTab.data);
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === "w" &&
        activeTab &&
        tabs.length > 1
      ) {
        e.preventDefault();
        handleTabClose(activeTab.id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeTab,
    tabs,
    addTabWithMedia,
    duplicateTabWithMedia,
    handleTabClose,
    handleSaveProduct,
  ]);

  // Active tab media
  const activeTabMedia = activeTabId
    ? getTabMedia(activeTabId)
    : {
        displayMedia: [],
        mediaFiles: {
          images: [],
          videos: [],
          existingMedia: [],
          pendingRemovals: [],
        },
      };

  // Can save current tab?
  const canSaveCurrentTab = useMemo(() => {
    if (!activeTab) return false;
    return (
      activeTab.status === TAB_STATUS.DRAFT ||
      activeTab.status === TAB_STATUS.FAILED ||
      activeTab.status === TAB_STATUS.EDITED
    );
  }, [activeTab]);

  // Context value
  const contextValue = useMemo(
    () => ({
      tabs,
      activeTabId,
      activeTab,
      addTab: addTabWithMedia,
      removeTab: handleTabClose,
      duplicateTab: duplicateTabWithMedia,
      updateTabData,
      setActiveTab,
      handleSaveProduct,
      handleReSave,
      categories,
      branches,
      user,
      displayMedia: activeTabMedia.displayMedia,
      mediaFiles: activeTabMedia.mediaFiles,
      handleMediaChange,
      handleMediaUpload,
      handleMediaDelete,
      canSaveCurrentTab,
      TAB_STATUS,
    }),
    [
      tabs,
      activeTabId,
      activeTab,
      categories,
      branches,
      user,
      activeTabMedia,
      handleMediaChange,
      handleMediaUpload,
      handleMediaDelete,
      addTabWithMedia,
      handleTabClose,
      duplicateTabWithMedia,
      updateTabData,
      setActiveTab,
      handleSaveProduct,
      handleReSave,
      canSaveCurrentTab,
    ]
  );

  return (
    <MultiProductContext.Provider value={contextValue}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto">
            {/* Top Bar */}
            <div className="flex items-center justify-between px-3 sm:px-4 lg:px-6 h-14 sm:h-16">
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => {
                    if (hasUnsavedChanges || stats.pending > 0) {
                      setConfirmDialog({
                        type: "navigate-away",
                        title: "Unsaved Changes",
                        message:
                          "You have unsaved products. Are you sure you want to leave?",
                        confirmText: "Leave",
                        cancelText: "Stay",
                        onConfirm: () => navigate("/products"),
                      });
                    } else {
                      navigate("/products");
                    }
                  }}
                  className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className="text-base sm:text-lg font-semibold text-gray-900">
                    Create Products
                  </h1>
                  <p className="text-xs text-gray-500 hidden sm:block">
                    {tabs.length} products •{" "}
                    {tabs.filter((t) => t.status === TAB_STATUS.DRAFT).length}{" "}
                    drafts
                    {attentionCount > 0 && (
                      <span className="text-amber-600 ml-1">
                        • {attentionCount} need attention
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Desktop Queue Status */}
              <div className="hidden lg:flex items-center gap-6">
                <QueueStatus
                  stats={stats}
                  onClick={() => setShowQueuePanel(true)}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setShowTemplates(true)}
                  className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <FileText size={16} />
                  <span className="hidden md:inline">Templates</span>
                </button>

                <button
                  onClick={() => setShowQueuePanel(true)}
                  className="lg:hidden relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Layers size={20} />
                  {(stats.pending > 0 || stats.failed > 0) && (
                    <span
                      className={`absolute -top-1 -right-1 w-5 h-5 text-white text-xs rounded-full flex items-center justify-center ${
                        stats.failed > 0 ? "bg-red-500" : "bg-amber-500"
                      }`}
                    >
                      {stats.pending + stats.failed}
                    </span>
                  )}
                </button>

                <button
                  onClick={handleSaveAll}
                  disabled={getSaveableTabs().length === 0}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  <Save size={16} />
                  <span className="hidden sm:inline">Save All</span>
                  {getSaveableTabs().length > 0 && (
                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                      {getSaveableTabs().length}
                    </span>
                  )}
                </button>

                {/* Menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <MoreHorizontal size={20} />
                  </button>
                  {showMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowMenu(false)}
                      />
                      <div className="absolute right-0 top-full mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                        <button
                          onClick={() => {
                            setShowTemplates(true);
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <FileText size={16} />
                          Load Template
                        </button>
                        <button
                          onClick={() => {
                            setShowBulkDuplicate(true);
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Copy size={16} />
                          Bulk Duplicate
                        </button>
                        <hr className="my-1 border-gray-100" />
                        <button
                          onClick={() => {
                            clearCompleted();
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <RefreshCw size={16} />
                          Clear Completed
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs - Scrollable */}
            <div className="flex items-center gap-1 px-3 sm:px-4 lg:px-6 pb-2 overflow-x-auto scrollbar-hide">
              {tabs.map((tab, index) => (
                <ProductTab
                  key={tab.id}
                  tab={tab}
                  index={index}
                  isActive={tab.id === activeTabId}
                  onClick={() => setActiveTab(tab.id)}
                  onClose={() => handleTabClose(tab.id)}
                  onDuplicate={() => duplicateTabWithMedia(tab.id)}
                  onReSave={() => handleReSave(tab.id)}
                  canClose={tabs.length > 1}
                />
              ))}
              <button
                onClick={() => addTabWithMedia()}
                className="flex-shrink-0 flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg ml-1"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">New</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
          {activeTab && (
            <QuickProductForm
              key={activeTab.id}
              tabId={activeTab.id}
              initialData={activeTab.data}
              status={activeTab.status}
              lastError={activeTab.lastError}
              onSave={(data) => handleSaveProduct(activeTab.id, data)}
              onDataChange={(data) => updateTabData(activeTab.id, data)}
              onReSave={() => handleReSave(activeTab.id)}
            />
          )}
        </main>

        {/* Mobile Bottom Bar */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-3 py-2 z-30 safe-area-bottom">
          <div className="flex items-center gap-2">
            <button
              onClick={() => addTabWithMedia()}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm"
            >
              <Plus size={18} />
              New
            </button>
            {canSaveCurrentTab && (
              <button
                onClick={() => handleSaveProduct(activeTab.id, activeTab.data)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-900 text-white rounded-lg font-medium text-sm"
              >
                <Save size={18} />
                {activeTab?.status === TAB_STATUS.FAILED ? "Retry" : "Save"}
              </button>
            )}
          </div>
        </div>
        <div className="lg:hidden h-16" />

        {/* Modals */}
        {showTemplates && (
          <ProductTemplates
            onSelect={handleSelectTemplate}
            onClose={() => setShowTemplates(false)}
            currentData={activeTab?.data}
          />
        )}

        {showQueuePanel && (
          <SaveQueuePanel
            queue={queue}
            stats={stats}
            isProcessing={isProcessing}
            isPaused={isPaused}
            onPause={pauseQueue}
            onResume={resumeQueue}
            onRetry={retryItem}
            onRemove={removeFromQueue}
            onClearCompleted={clearCompleted}
            onClose={() => setShowQueuePanel(false)}
          />
        )}

        {showBulkDuplicate && (
          <BulkDuplicateModal
            productName={activeTab?.data?.name || "Current Product"}
            count={duplicateCount}
            onCountChange={setDuplicateCount}
            onConfirm={handleBulkDuplicate}
            onClose={() => setShowBulkDuplicate(false)}
          />
        )}

        {/* Confirmation Dialog */}
        {confirmDialog && (
          <ConfirmDialog
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmText={confirmDialog.confirmText}
            cancelText={confirmDialog.cancelText}
            onConfirm={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
              handleConfirmDialogAction(true);
            }}
            onCancel={() => handleConfirmDialogAction(false)}
          />
        )}
      </div>
    </MultiProductContext.Provider>
  );
};

// Tab Component - IMPROVED with status indicators
const ProductTab = memo(
  ({
    tab,
    index,
    isActive,
    onClick,
    onClose,
    onDuplicate,
    onReSave,
    canClose,
  }) => {
    const statusConfig = {
      [TAB_STATUS.DRAFT]: { icon: FileText, color: "text-gray-400", bg: "" },
      [TAB_STATUS.QUEUED]: {
        icon: Clock,
        color: "text-amber-500",
        bg: "bg-amber-50",
      },
      [TAB_STATUS.SAVING]: {
        icon: Loader2,
        color: "text-blue-500",
        bg: "bg-blue-50",
        spin: true,
      },
      [TAB_STATUS.SAVED]: {
        icon: CheckCircle2,
        color: "text-emerald-500",
        bg: "bg-emerald-50",
      },
      [TAB_STATUS.FAILED]: {
        icon: XCircle,
        color: "text-red-500",
        bg: "bg-red-50",
      },
      [TAB_STATUS.EDITED]: {
        icon: Edit3,
        color: "text-amber-500",
        bg: "bg-amber-50",
      },
    };

    const config = statusConfig[tab.status] || statusConfig[TAB_STATUS.DRAFT];
    const Icon = config.icon;

    const showReSaveButton =
      tab.status === TAB_STATUS.FAILED || tab.status === TAB_STATUS.EDITED;

    return (
      <div className="relative flex-shrink-0">
        <button
          onClick={onClick}
          className={`group flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            isActive
              ? "bg-gray-900 text-white"
              : config.bg
              ? `${config.bg} ${config.color}`
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          <Icon
            size={14}
            className={`${isActive ? "text-white" : config.color} ${
              config.spin ? "animate-spin" : ""
            }`}
          />
          <span className="max-w-[60px] sm:max-w-[100px] truncate">
            {tab.data.name || `Product ${index + 1}`}
          </span>

          {/* Action buttons on hover */}
          <div className="hidden sm:flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100">
            {showReSaveButton && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReSave();
                }}
                className={`p-0.5 rounded ${
                  isActive ? "hover:bg-white/20" : "hover:bg-gray-300"
                }`}
                title="Retry save"
              >
                <RefreshCw size={12} />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              className={`p-0.5 rounded ${
                isActive ? "hover:bg-white/20" : "hover:bg-gray-300"
              }`}
              title="Duplicate"
            >
              <Copy size={12} />
            </button>
            {canClose && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className={`p-0.5 rounded ${
                  isActive ? "hover:bg-white/20" : "hover:bg-gray-300"
                }`}
                title="Close"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </button>

        {/* Error indicator dot */}
        {tab.status === TAB_STATUS.FAILED && !isActive && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
        )}
      </div>
    );
  }
);
ProductTab.displayName = "ProductTab";

// Queue Status - IMPROVED with attention indicator
const QueueStatus = memo(({ stats, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-4 px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg"
  >
    {stats.saving > 0 && (
      <div className="flex items-center gap-2 text-blue-600">
        <Loader2 size={14} className="animate-spin" />
        <span className="text-sm font-medium">{stats.saving} saving</span>
      </div>
    )}
    {stats.pending > 0 && (
      <div className="flex items-center gap-2 text-amber-600">
        <Clock size={14} />
        <span className="text-sm font-medium">{stats.pending} queued</span>
      </div>
    )}
    {stats.completed > 0 && (
      <div className="flex items-center gap-2 text-emerald-600">
        <CheckCircle2 size={14} />
        <span className="text-sm font-medium">{stats.completed} saved</span>
      </div>
    )}
    {stats.failed > 0 && (
      <div className="flex items-center gap-2 text-red-600">
        <XCircle size={14} />
        <span className="text-sm font-medium">{stats.failed} failed</span>
      </div>
    )}
    {stats.edited > 0 && (
      <div className="flex items-center gap-2 text-amber-600">
        <Edit3 size={14} />
        <span className="text-sm font-medium">{stats.edited} edited</span>
      </div>
    )}
    {stats.total === 0 && (
      <span className="text-sm text-gray-500">No items in queue</span>
    )}
  </button>
));
QueueStatus.displayName = "QueueStatus";

// Confirmation Dialog Component
const ConfirmDialog = memo(
  ({ title, message, confirmText, cancelText, onConfirm, onCancel }) => (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-amber-100 rounded-lg">
            <AlertTriangle size={20} className="text-amber-600" />
          </div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-5">{message}</p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 text-sm"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 text-white bg-red-600 rounded-lg font-medium hover:bg-red-700 text-sm"
          >
            {confirmText}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.15s ease-out;
        }
      `}</style>
    </div>
  )
);
ConfirmDialog.displayName = "ConfirmDialog";

// Bulk Duplicate Modal
const BulkDuplicateModal = ({
  productName,
  count,
  onCountChange,
  onConfirm,
  onClose,
}) => (
  <div
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2.5 bg-gray-100 rounded-lg">
          <Copy size={20} className="text-gray-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Bulk Duplicate</h3>
          <p className="text-xs text-gray-500 truncate max-w-[200px]">
            Create copies of "{productName}"
          </p>
        </div>
      </div>
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Number of copies
        </label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onCountChange(Math.max(1, count - 1))}
            className="p-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-lg font-medium"
          >
            −
          </button>
          <input
            type="number"
            value={count}
            onChange={(e) =>
              onCountChange(
                Math.max(1, Math.min(20, parseInt(e.target.value) || 1))
              )
            }
            className="flex-1 text-center text-xl font-semibold py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
            min="1"
            max="20"
          />
          <button
            onClick={() => onCountChange(Math.min(20, count + 1))}
            className="p-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-lg font-medium"
          >
            +
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 py-2.5 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 py-2.5 text-white bg-gray-900 rounded-lg font-medium hover:bg-gray-800 text-sm"
        >
          Create {count} Copies
        </button>
      </div>
    </div>
  </div>
);

export default MultiProductCreator;
